import React, { useState, useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import { ArrowUpCircle, ArrowDownCircle, Plus, Search, AlertCircle, Loader2, Check, Edit2, Trash2, DollarSign, Building2, Calendar, Paperclip, RotateCcw, History, X } from 'lucide-react';
import financeService from '../services/finance';
import cabangService from '../services/cabang';
import { useAuth } from '../context/AuthContext';
import DeleteConfirmModal from './DeleteConfirmModal';

const formatRupiah = (num) => new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(num || 0);

const TransaksiFinance = ({ onAdd, onEdit }) => {
  const { user } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [akunList, setAkunList] = useState([]);
  const [cabangList, setCabangList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState({ totalPemasukan: 0, totalPengeluaran: 0, saldoAkhir: 0 });
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTipe, setFilterTipe] = useState('All');
  const [filterAkun, setFilterAkun] = useState('All');
  const [filterCabang, setFilterCabang] = useState('All');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  // Deleted transactions state
  const [showDeleted, setShowDeleted] = useState(false);
  const [deletedTransactions, setDeletedTransactions] = useState([]);
  const [deletedLoading, setDeletedLoading] = useState(false);
  const [restoreConfirm, setRestoreConfirm] = useState(null);
  const [isRestoring, setIsRestoring] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page, limit: 20 };
      if (searchTerm) params.search = searchTerm;
      if (filterTipe !== 'All') params.tipe = filterTipe;
      if (filterAkun !== 'All') params.akunId = filterAkun;
      if (filterCabang !== 'All') params.cabangId = filterCabang;
      if (dateRange.start) params.startDate = dateRange.start;
      if (dateRange.end) params.endDate = dateRange.end;
      const data = await financeService.getTransaksi(params);
      setTransactions(data.transactions || []);
      if (data.summary) setSummary(data.summary);
      if (data.pagination) setPagination(data.pagination);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [page, searchTerm, filterTipe, filterAkun, filterCabang, dateRange]);

  const fetchDeleted = useCallback(async () => {
    if (!showDeleted) return;
    try {
      setDeletedLoading(true);
      const data = await financeService.getDeletedTransaksi({ limit: 50 });
      setDeletedTransactions(data.transactions || []);
    } catch (err) {
      console.error('Failed to fetch deleted:', err);
    } finally {
      setDeletedLoading(false);
    }
  }, [showDeleted]);

  const fetchAkun = useCallback(async () => {
    try {
      const data = await financeService.getAkun({ isActive: 'true' });
      setAkunList(data.akun || []);
    } catch (err) { }
  }, []);

  const fetchCabang = useCallback(async () => {
    try {
      const data = await cabangService.getCabang();
      setCabangList(data.cabang || []);
    } catch (err) { }
  }, []);

  useEffect(() => { fetchData(); fetchAkun(); fetchCabang(); }, [fetchData, fetchAkun, fetchCabang]);
  useEffect(() => { if (showDeleted) fetchDeleted(); }, [showDeleted, fetchDeleted]);

  const showSuccess = (msg) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm || isDeleting) return;
    setIsDeleting(true);
    try {
      await financeService.deleteTransaksi(deleteConfirm._id);
      setDeleteConfirm(null);
      await fetchData();
      showSuccess('Transaksi berhasil dihapus');
    } catch (err) {
      setError(err.message);
      setDeleteConfirm(null);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleRestoreConfirm = async () => {
    if (!restoreConfirm || isRestoring) return;
    setIsRestoring(true);
    try {
      await financeService.restoreTransaksi(restoreConfirm._id);
      setRestoreConfirm(null);
      showSuccess('Transaksi berhasil dikembalikan');
      // Refresh both lists
      await fetchData();
      await fetchDeleted();
    } catch (err) {
      setError(err.message);
      setRestoreConfirm(null);
    } finally {
      setIsRestoring(false);
    }
  };

  return (
    <div className="space-y-4 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Transaksi Keuangan</h2>
          <p className="mt-1 text-sm text-gray-500">Record all income and expenses</p>
        </div>
        <div className="flex items-center space-x-2">
          {user?.role === 'admin' && (
            <button onClick={() => setShowDeleted(!showDeleted)}
              className={`btn-secondary text-sm ${showDeleted ? 'border-amber-300 text-amber-700 bg-amber-50' : ''}`}>
              <History className="w-4 h-4" />
              <span>Riwayat Hapus</span>
            </button>
          )}
          <button onClick={onAdd} className="btn-primary text-sm">
            <Plus className="w-4 h-4" /><span>Add Transaksi</span>
          </button>
        </div>
      </div>

      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 animate-slide-up">
          <div className="flex items-center space-x-2"><Check className="w-5 h-5 text-green-500" /><p className="text-sm text-green-800">{successMessage}</p></div>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card p-4">
          <div className="flex items-center space-x-3"><div className="p-2.5 rounded-xl bg-green-50"><ArrowUpCircle className="w-5 h-5 text-green-600" /></div><div><p className="text-xs text-gray-500">Total Pemasukan</p><p className="text-xl font-bold text-green-600">{formatRupiah(summary.totalPemasukan)}</p></div></div>
        </div>
        <div className="card p-4">
          <div className="flex items-center space-x-3"><div className="p-2.5 rounded-xl bg-red-50"><ArrowDownCircle className="w-5 h-5 text-red-600" /></div><div><p className="text-xs text-gray-500">Total Pengeluaran</p><p className="text-xl font-bold text-red-600">{formatRupiah(summary.totalPengeluaran)}</p></div></div>
        </div>
        <div className="card p-4">
          <div className="flex items-center space-x-3"><div className={`p-2.5 rounded-xl ${summary.saldoAkhir >= 0 ? 'bg-blue-50' : 'bg-red-50'}`}><DollarSign className={`w-5 h-5 ${summary.saldoAkhir >= 0 ? 'text-blue-600' : 'text-red-600'}`} /></div><div><p className="text-xs text-gray-500">Saldo Akhir</p><p className={`text-xl font-bold ${summary.saldoAkhir >= 0 ? 'text-blue-600' : 'text-red-600'}`}>{formatRupiah(summary.saldoAkhir)}</p></div></div>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="px-4 py-3 space-y-3">
          <div className="flex items-center space-x-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input type="text" placeholder="Search..." value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-8 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 bg-gray-50 hover:bg-white focus:bg-white" />
            </div>
            <select value={filterTipe} onChange={e => setFilterTipe(e.target.value)} className="select-field text-sm">
              <option value="All">All Types</option><option value="PEMASUKAN">Pemasukan</option><option value="PENGELUARAN">Pengeluaran</option>
            </select>
            <select value={filterAkun} onChange={e => setFilterAkun(e.target.value)} className="select-field text-sm">
              <option value="All">All Akun</option>
              {akunList.map(a => <option key={a._id} value={a._id}>{a.kodeAkun} - {a.namaAkun}</option>)}
            </select>
            <select value={filterCabang} onChange={e => setFilterCabang(e.target.value)} className="select-field text-sm">
              <option value="All">All Cabang</option>
              {cabangList.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <input type="date" value={dateRange.start} onChange={e => setDateRange(p => ({ ...p, start: e.target.value }))} className="input-field text-sm" />
            <input type="date" value={dateRange.end} onChange={e => setDateRange(p => ({ ...p, end: e.target.value }))} className="input-field text-sm" />
          </div>
        </div>
      </div>

      {/* Transaction List */}
      <div className="space-y-2">
        {loading ? (
          <div className="flex flex-col items-center py-20"><Loader2 className="h-10 w-10 animate-spin text-blue-600" /><span className="mt-4 text-gray-500">Loading...</span></div>
        ) : transactions.length > 0 ? (
          transactions.map((tx, idx) => (
            <div key={tx._id} className="card p-4 hover:shadow-md transition-all animate-slide-up" style={{ animationDelay: `${idx * 20}ms` }}>
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-3 flex-1">
                  <div className={`flex-shrink-0 w-10 h-10 rounded-xl ${tx.tipeTransaksi === 'PEMASUKAN' ? 'bg-green-50' : 'bg-red-50'} flex items-center justify-center`}>
                    {tx.tipeTransaksi === 'PEMASUKAN' ? <ArrowUpCircle className="w-5 h-5 text-green-600" /> : <ArrowDownCircle className="w-5 h-5 text-red-600" />}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <span className="text-xs font-mono text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">{tx.nomorTransaksi}</span>
                      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${tx.tipeTransaksi === 'PEMASUKAN' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{tx.tipeTransaksi}</span>
                      <span className="text-[10px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">{tx.kategori}</span>
                    </div>
                    <p className="text-sm font-semibold text-gray-900 mt-0.5">{tx.deskripsi}</p>
                    <div className="flex items-center space-x-3 mt-1 text-xs text-gray-400">
                      <span className="flex items-center"><Building2 className="w-3 h-3 mr-1" />{tx.akun?.namaAkun}</span>
                      <span className="flex items-center"><Calendar className="w-3 h-3 mr-1" />{format(new Date(tx.tanggal), 'dd MMM yyyy')}</span>
                      {tx.metodePembayaran && <span>{tx.metodePembayaran}</span>}
                      {tx.referensi && <span>Ref: {tx.referensi}</span>}
                      {tx.cabang?.namaCabang && <span className="text-amber-600 font-medium">📍 {tx.cabang.namaCabang}</span>}
                      {tx.lampiran && (
                        <a
                          href={`http://localhost:5000${tx.lampiran}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-0.5 text-blue-500 hover:text-blue-700"
                          title="View attachment"
                        >
                          <Paperclip className="w-3 h-3" />
                        </a>
                      )}
                      {tx.createdBy?.name && <span>by {tx.createdBy.name}</span>}
                    </div>
                  </div>
                </div>
                <div className="text-right ml-3">
                  <p className={`text-lg font-bold ${tx.tipeTransaksi === 'PEMASUKAN' ? 'text-green-600' : 'text-red-600'}`}>
                    {tx.tipeTransaksi === 'PEMASUKAN' ? '+' : '-'}{formatRupiah(tx.jumlah)}
                  </p>
                  <div className="flex space-x-1 mt-1 justify-end">
                    <button onClick={() => onEdit(tx)}
                      className="p-1.5 text-gray-400 hover:text-indigo-600 rounded-lg" title="Edit"><Edit2 className="w-3.5 h-3.5" /></button>
                    {user?.role === 'admin' && (
                      <button onClick={() => setDeleteConfirm(tx)}
                        className="p-1.5 text-gray-400 hover:text-red-600 rounded-lg" title="Hapus Riwayat"><Trash2 className="w-3.5 h-3.5" /></button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="card p-12 text-center">
            <DollarSign className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">No transactions yet</p>
            <p className="text-gray-400 text-sm mt-1">Add your first income or expense transaction</p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="flex justify-between items-center card px-4 py-3">
          <span className="text-sm text-gray-500">Page {pagination.currentPage} of {pagination.totalPages}</span>
          <div className="flex space-x-2">
            <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="btn-secondary text-xs disabled:opacity-50">Previous</button>
            <button disabled={page >= pagination.totalPages} onClick={() => setPage(p => p + 1)} className="btn-secondary text-xs disabled:opacity-50">Next</button>
          </div>
        </div>
      )}

      <DeleteConfirmModal
        isOpen={deleteConfirm !== null}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={handleDeleteConfirm}
        title="Hapus Transaksi"
        isLoading={isDeleting}
      >
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500 font-medium">No. Transaksi</span>
          <span className="text-xs font-mono font-bold text-gray-900">{deleteConfirm?.nomorTransaksi}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500 font-medium">Deskripsi</span>
          <span className="text-xs font-semibold text-gray-900 text-right max-w-[200px]">{deleteConfirm?.deskripsi}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500 font-medium">Tipe</span>
          <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${deleteConfirm?.tipeTransaksi === 'PEMASUKAN' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{deleteConfirm?.tipeTransaksi}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500 font-medium">Jumlah</span>
          <span className={`text-sm font-bold ${deleteConfirm?.tipeTransaksi === 'PEMASUKAN' ? 'text-green-600' : 'text-red-600'}`}>
            {deleteConfirm?.tipeTransaksi === 'PEMASUKAN' ? '+' : '-'}{formatRupiah(deleteConfirm?.jumlah || 0)}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500 font-medium">Akun</span>
          <span className="text-xs font-semibold text-gray-900">{deleteConfirm?.akun?.namaAkun}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500 font-medium">Tanggal</span>
          <span className="text-xs font-semibold text-gray-900">{deleteConfirm?.tanggal ? format(new Date(deleteConfirm.tanggal), 'dd MMM yyyy') : '-'}</span>
        </div>
        {deleteConfirm?.cabang?.namaCabang && (
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500 font-medium">Cabang</span>
            <span className="text-xs font-semibold text-amber-600">{deleteConfirm.cabang.namaCabang}</span>
          </div>
        )}
      </DeleteConfirmModal>

      {/* Deleted Transactions Section */}
      {showDeleted && (
        <div className="space-y-3 animate-slide-up">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-bold text-amber-700 flex items-center space-x-2">
              <History className="w-5 h-5" />
              <span>Riwayat Transaksi Dihapus</span>
            </h3>
            <button onClick={() => setShowDeleted(false)} className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg">
              <X className="w-4 h-4" />
            </button>
          </div>

          {deletedLoading ? (
            <div className="flex flex-col items-center py-12"><Loader2 className="h-8 w-8 animate-spin text-amber-500" /><span className="mt-3 text-sm text-gray-500">Loading...</span></div>
          ) : deletedTransactions.length > 0 ? (
            deletedTransactions.map((tx, idx) => (
              <div key={tx._id} className="card p-4 border-amber-200 border-2 bg-amber-50/30 animate-slide-up" style={{ animationDelay: `${idx * 30}ms` }}>
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3 flex-1">
                    <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-gray-200 flex items-center justify-center">
                      {tx.tipeTransaksi === 'PEMASUKAN' ? <ArrowUpCircle className="w-5 h-5 text-gray-500" /> : <ArrowDownCircle className="w-5 h-5 text-gray-500" />}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <span className="text-xs font-mono text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">{tx.nomorTransaksi}</span>
                        <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-red-100 text-red-700">DIHAPUS</span>
                        <span className="text-[10px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">{tx.kategori}</span>
                      </div>
                      <p className="text-sm font-semibold text-gray-900 mt-0.5">{tx.deskripsi}</p>
                      <div className="flex items-center space-x-3 mt-1 text-xs text-gray-400">
                        <span className="flex items-center"><Building2 className="w-3 h-3 mr-1" />{tx.akun?.namaAkun}</span>
                        <span className="flex items-center"><Calendar className="w-3 h-3 mr-1" />{format(new Date(tx.tanggal), 'dd MMM yyyy')}</span>
                        <span>Dihapus {tx.updatedAt ? format(new Date(tx.updatedAt), 'dd MMM HH:mm') : ''}</span>
                      </div>
                    </div>
                  </div>
                  <div className="text-right ml-3">
                    <p className={`text-lg font-bold ${tx.tipeTransaksi === 'PEMASUKAN' ? 'text-green-600' : 'text-red-600'}`}>
                      {tx.tipeTransaksi === 'PEMASUKAN' ? '+' : '-'}{formatRupiah(tx.jumlah)}
                    </p>
                    <button
                      onClick={() => setRestoreConfirm(tx)}
                      className="mt-2 px-3 py-1.5 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white text-xs font-semibold rounded-lg hover:shadow-md transition-all flex items-center space-x-1"
                    >
                      <RotateCcw className="w-3 h-3" />
                      <span>Restore</span>
                    </button>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="card p-12 text-center">
              <History className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 font-medium">Tidak ada riwayat hapus</p>
              <p className="text-gray-400 text-sm mt-1">Transaksi yang dihapus akan muncul di sini</p>
            </div>
          )}
        </div>
      )}

      {/* Restore Confirmation Modal */}
      {restoreConfirm && (
        <div className="fixed inset-0 z-50 overflow-y-auto animate-fade-in" onClick={() => { if (!isRestoring) { setRestoreConfirm(null); } }}>
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20">
            <div className="fixed inset-0 bg-black/40 backdrop-blur-sm transition-opacity" />
            <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-auto z-10 animate-scale-in p-6" onClick={e => e.stopPropagation()}>
              <div className="flex items-center space-x-3 mb-4">
                <div className="p-2.5 rounded-xl bg-emerald-50">
                  <RotateCcw className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Restore Transaksi</h3>
                  <p className="text-xs text-gray-500">Saldo akun akan dikembalikan seperti semula</p>
                </div>
              </div>

              <div className="bg-gray-50 rounded-xl p-4 space-y-2 mb-4">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500 font-medium">No. Transaksi</span>
                  <span className="text-xs font-mono font-bold text-gray-900">{restoreConfirm?.nomorTransaksi}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500 font-medium">Deskripsi</span>
                  <span className="text-xs font-semibold text-gray-900 text-right max-w-[200px]">{restoreConfirm?.deskripsi}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500 font-medium">Tipe</span>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${restoreConfirm?.tipeTransaksi === 'PEMASUKAN' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{restoreConfirm?.tipeTransaksi}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500 font-medium">Jumlah</span>
                  <span className={`text-sm font-bold ${restoreConfirm?.tipeTransaksi === 'PEMASUKAN' ? 'text-green-600' : 'text-red-600'}`}>
                    {restoreConfirm?.tipeTransaksi === 'PEMASUKAN' ? '+' : '-'}{formatRupiah(restoreConfirm?.jumlah || 0)}
                  </span>
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <button onClick={() => setRestoreConfirm(null)} disabled={isRestoring} className="btn-secondary text-sm">
                  <X className="w-4 h-4" />
                  <span>Batal</span>
                </button>
                <button onClick={handleRestoreConfirm} disabled={isRestoring}
                  className="px-4 py-2.5 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white text-sm font-semibold rounded-xl hover:shadow-md transition-all flex items-center space-x-1.5 disabled:opacity-50">
                  {isRestoring ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /><span>Memproses...</span></>
                  ) : (
                    <><RotateCcw className="w-4 h-4" /><span>Restore Transaksi</span></>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default TransaksiFinance;
